import {hpcAttrs} from '@github-ui/hpc'
import {ChartCard} from '@github-ui/chart-card'
import {Link} from '@primer/react'
import styles from './PulseSummary.module.css'
import type {PulseAuthorCommitsDataPoint, PulseDiffstatSummary} from '../types'
import {hasHave, pluralize} from '../helpers/infographic-helpers'
import {SkeletonLoaderSummary, SkeletonLoaderChart} from './loaders/SummaryLoaders'
import {BlankslateCommits} from './BlankslateCommits'
import {ErrorMessage} from './ErrorMessage'
import {useParams} from '@github-ui/react-router'
import {hovercardAttributesForActor} from '@github-ui/hovercards'
import {clsx} from 'clsx'

interface PulseSummaryProps {
  committerData: PulseAuthorCommitsDataPoint[]
  diffstatSummary: PulseDiffstatSummary
  summaryLoaded: boolean
  chartLoaded: boolean
  summaryError?: string
  chartError?: string
}

export function PulseSummary({
  committerData,
  diffstatSummary,
  summaryLoaded,
  chartLoaded,
  summaryError,
  chartError,
}: PulseSummaryProps) {
  const {owner, repo} = useParams()

  const hasActualDiffstatData =
    typeof diffstatSummary.additions === 'number' && typeof diffstatSummary.totalCommits === 'number'
  return !hasActualDiffstatData ? (
    <BlankslateCommits owner={owner} repo={repo} forkPathUrl={diffstatSummary.forkPathUrl} />
  ) : (
    <div className={styles.container}>
      <div className={styles.summaryBox}>
        <div>
          <h3 className={styles.title} {...hpcAttrs()}>
            Summary
          </h3>
        </div>
        {summaryLoaded && hasActualDiffstatData && !summaryError ? (
          <>
            <div>
              <p className={styles.text}>
                <span>Excluding merges, </span>
                <strong className={styles.strong}>
                  {diffstatSummary.authorsWithCommits} {pluralize(diffstatSummary.authorsWithCommits, 'author')}
                </strong>
                <span> {hasHave(diffstatSummary.authorsWithCommits)} pushed </span>
                <strong className={styles.strong}>
                  <span>
                    {diffstatSummary.masterCommits} {pluralize(diffstatSummary.masterCommits, 'commit')}
                  </span>
                </strong>
                <span> to {diffstatSummary.defaultBranch} and </span>
                <strong className={styles.strong}>
                  {diffstatSummary.totalCommits} {pluralize(diffstatSummary.totalCommits, 'commit')}
                </strong>
                <span> to all branches.</span>
              </p>
            </div>
            <div>
              <p className={`${styles.text} color-fg-muted`}>
                <span>On {diffstatSummary.defaultBranch}, </span>
                <strong className="color-fg-default">
                  {diffstatSummary.filesChanged} {pluralize(diffstatSummary.filesChanged, 'file')}
                </strong>
                <span> {hasHave(diffstatSummary.filesChanged)} changed and there have been </span>
                {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
                <Link href={diffstatSummary.comparePathUrl} className="lines-changed Link--muted Link--inTextBlock">
                  <strong className="color-fg-success">{diffstatSummary.additions.toLocaleString()}</strong>{' '}
                  <strong className="color-fg-default">{pluralize(diffstatSummary.additions, 'addition')}</strong>
                  <span> and </span>
                  <strong className="color-fg-danger">{diffstatSummary.deletions.toLocaleString()}</strong>{' '}
                  <strong className="color-fg-default">{pluralize(diffstatSummary.deletions, 'deletion')}</strong>
                </Link>
              </p>
            </div>
          </>
        ) : summaryError ? (
          <div className={`d-flex flex-items-center flex-justify-center`}>
            <ErrorMessage errorMessage={summaryError} />
          </div>
        ) : (
          <SkeletonLoaderSummary />
        )}
      </div>
      <div className={styles.chartContainer}>
        {chartLoaded && !chartError ? (
          <ChartCard size="small" border padding="normal" visibleControls>
            <ChartCard.Title as="h2">Top committers</ChartCard.Title>
            <ChartCard.ColumnChart
              series={[
                {
                  name: 'Commits',
                  data: committerData.slice(0, 15).map(user => user.commits), // Show top 15 committers (NUM_AUTHORS = 15)
                },
              ]}
              theme="green"
              options={{
                yAxis: {
                  title: 'Commits',
                  gridLineWidth: 1,
                  allowDecimals: false,
                  min: 0,
                  max: Math.max(...committerData.slice(0, 15).map(user => user.commits)),
                  labels: {
                    formatter() {
                      // Match D3 format: numbers < 1000 show as-is, >= 1000 use SI prefixes
                      const val = typeof this.value === 'string' ? parseFloat(this.value) : this.value
                      return val < 1000 ? val.toString() : `${(val / 1000).toFixed(0)}k`
                    },
                  },
                },
                xAxis: {
                  title: '',
                  categories: committerData.slice(0, 15).map((user, index) => user.login || `User ${index}`),
                },
                plot: {
                  animation: false,
                },
              }}
              // NOTE: We use `overrideOptionsNotRecommended` here because the ChartCard API does not support rendering custom HTML (avatars) in x-axis labels.
              // This override is required to display user avatars for committers. If ChartCard adds support for custom label rendering, this should be refactored.
              overrideOptionsNotRecommended={{
                xAxis: {
                  gridLineWidth: 0,
                  categories: committerData.slice(0, 15).map((user, index) => user.login || `User ${index}`),
                  labels: {
                    useHTML: true,
                    style: {
                      textAlign: 'center',
                    },
                    // Force all labels to show, prevent automatic spacing
                    step: 1,
                    staggerLines: 1,
                    allowOverlap: false,
                    autoRotation: [],
                    formatter() {
                      // Use this.pos which is the actual position in the chart
                      const categoryIndex = this.pos
                      const user = committerData.slice(0, 15)[categoryIndex]
                      if (!user) return ''

                      const avatarSrc = user.gravatar || '/images/icons/copilot/cp-head-square.png'
                      const avatarSize = 16
                      const avatarUrl =
                        avatarSrc.startsWith('http://') || avatarSrc.startsWith('https://')
                          ? {href: avatarSrc}
                          : new URL(avatarSrc, window.location.origin)

                      const userProfileUrl = user.login ? `/${user.login}` : '#'

                      // Generate hovercard attributes using the official utility
                      const hovercardAttrs = user.login ? hovercardAttributesForActor(user.login) : {}
                      const hovercardAttrString = Object.entries(hovercardAttrs)
                        .map(([key, value]) => `${key}="${value}"`)
                        .join(' ')

                      // eslint-disable-next-line github/unescaped-html-literal
                      return `
                    <div style="text-align: center; margin-top: 0px; height: auto; padding: 0 1px;">
                      <a href="${userProfileUrl}"
                         style="display: block; text-decoration: none;"
                         aria-label="${user.login || 'Unknown user'}"
                         ${hovercardAttrString}>
                        <img src="${avatarUrl.href}"
                             style="
                               height: ${avatarSize}px;
                               width: ${avatarSize}px;
                               border-radius: 1px;
                               display: block;
                               object-fit: cover;
                               margin: 0 auto;
                               border: 1px solid var(--borderColor-default, var(--color-border-default, #d0d7de));
                               background-color: var(--bgColor-default, var(--color-canvas-default, #ffffff));
                               cursor: pointer;
                             "
                             alt="${user.login || ''}"
                             role="img"
                             />
                      </a>
                    </div>`
                    },
                  },
                  lineWidth: 1,
                  tickLength: 0,
                  min: 0,
                  max: 14,
                },
                tooltip: {
                  formatter() {
                    const categoryIndex = this.index
                    const user = committerData.slice(0, 15)[categoryIndex]
                    const author = user?.login || user?.name || 'Unknown'
                    const commits = this.y
                    const commitText = commits === 1 ? 'commit' : 'commits'

                    // Match the original D3 tooltip format (text only for linting compliance)
                    return `${commits} ${commitText} authored by ${author}`
                  },
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  style: {
                    color: 'white',
                    fontSize: '12px',
                    padding: '8px',
                  },
                  borderWidth: 0,
                  shadow: false,
                },
                plotOptions: {
                  column: {
                    pointPadding: 0.1,
                    groupPadding: 0.2,
                    borderWidth: 0,
                    states: {
                      hover: {
                        brightness: 0.1,
                      },
                    },
                  },
                },
              }}
            />
          </ChartCard>
        ) : chartError ? (
          <div className={clsx(styles.summaryBox, `d-flex flex-items-center flex-justify-center`)}>
            <ErrorMessage errorMessage={chartError} />
          </div>
        ) : (
          <SkeletonLoaderChart />
        )}
      </div>
    </div>
  )
}
